import React from "react";
import {
  Outlet,
  NavLink,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CorporateProvider, useCorporate } from "./CorporateContext";
import StatusBadge from "../../../Components/admin/shared/StatusBadge";
import { Button } from "../../../ui/button";
import { Card, CardContent } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { cn } from "../../../lib/utils";

const TABS = [
  { key: "overview", label: "Overview", path: "" },
  { key: "branding", label: "Branding", path: "branding" },
  { key: "content", label: "Content", path: "content" },
  { key: "departments", label: "Departments", path: "departments" },
  { key: "roles", label: "Roles", path: "roles" },
  { key: "members", label: "Members", path: "members" },
  { key: "benefits", label: "Benefits", path: "benefits" },
  { key: "subscription", label: "Subscription", path: "subscription" },
  { key: "dashboard", label: "Dashboard", path: "dashboard" },
  { key: "cost", label: "Cost", path: "cost" },
];

const DetailHeader = () => {
  const { corporate, loading, error } = useCorporate();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-4 w-48" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (!corporate) return null;

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/enterprises")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight">
              {corporate.display_name || corporate.name}
            </h1>
            <StatusBadge status={corporate.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            <code className="text-xs">/enterprise/{corporate.slug}</code>
            {corporate.support_email ? ` · ${corporate.support_email}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
};

const TabsNav = () => {
  const { id } = useParams();
  const location = useLocation();

  const isActive = (path) => {
    const base = `/admin/enterprises/${id}`;
    if (path === "") return location.pathname === base;
    return location.pathname.startsWith(`${base}/${path}`);
  };

  return (
    <div className="border-b">
      <nav className="-mb-px flex flex-wrap gap-2 overflow-x-auto">
        {TABS.map((tab) => {
          const target =
            tab.path === ""
              ? `/admin/enterprises/${id}`
              : `/admin/enterprises/${id}/${tab.path}`;
          return (
            <NavLink
              key={tab.key}
              to={target}
              end={tab.path === ""}
              className={cn(
                "whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                isActive(tab.path)
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};

const AdminCorporateDetail = () => {
  const { id } = useParams();

  return (
    <CorporateProvider id={id}>
      <div className="space-y-6">
        <DetailHeader />
        <Card>
          <CardContent className="pt-4">
            <TabsNav />
            <div className="pt-6">
              <Outlet />
            </div>
          </CardContent>
        </Card>
      </div>
    </CorporateProvider>
  );
};

export default AdminCorporateDetail;
